<template>
  <div class="groovemail-modal bg-secondary-darker text-secondary-invert">
    <div
      class="heading border-solid border-secondary border-b flex flex-row justify-between"
    >
      <div class="flex flex-column flex-grow items-center h-16">
        <span class="px-4 cursor-pointer" @click="close()">
          <CloseIcon class="h-6 w-6" />
        </span>
        <span class="capitalize flex-grow text-secondary-invert">
          {{ $t('frontend_customize') }}
        </span>
      </div>
    </div>
    <div class="groovemail-modal-body text-sm overflow-y-auto custom-scrollbars" style="height: calc(100vh - 131px);">
      <div class="flex justify-center p-4">
        <span class="text-sm font-semibold mr-2">{{ $t('frontend_advanced_mode') }}</span>
        <gd-toggle
          bgClass="bg-secondary"
          v-model="advancedMode"
        />
      </div>
      <expansion-group>
      <expansion-panel
        :title="$t('Form')"
        :flat="true"
      >
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_groovemember_portals_text_color') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.form.color"
            ></color-picker>
          </div>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_background') }}</div>
          <background-section
            @change="updateFormBackgroundValues(...arguments)"
            :styles="formBackgroundValues()"
          ></background-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('form', '', ...arguments)"
            :values="spacingValues('form')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_borders') }}</div>
          <border-section
            @change="updateBorder('form', '', ...arguments)"
            :styles="localStyles.form.border"
          ></border-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.form.customStyles"
            rows="4"
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="`Headings`"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.heading.text"
            :fontData="fontData"
            @change="updateText('heading', '', ...arguments)"
          ></text-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('heading', '', ...arguments)"
            :values="spacingValues('heading')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.heading.customStyles"
            rows="4"
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="`Heading: ${getTextElementTitle(field.id)}`"
        class="text-element-tab text-secondary-invert border-secondary"
        v-if="advancedMode && field.fieldtype == 'heading'"
        v-for="(field, index) in headings"
        :key="field.id"
        :flat="true"
      >
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='activateAdvancedField(field)'
          v-if="!localStyles.advanced.fields[field.id]"
        >{{ $t('add_customization') }}</gd-button>
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='deactivateAdvancedField(field.id)'
          v-if="localStyles.advanced.fields[field.id]"
        >{{ $t('remove_customization') }}</gd-button>
        <div v-if="localStyles.advanced.fields[field.id]">
          <div class="mb-4">
            <div class="mb-1">{{ $t('Text') }}</div>
            <text-section
              :styles="localStyles.advanced.fields[field.id].text"
              :fontData="fontData"
              @change="updateText('heading', field.id, ...arguments)"
            ></text-section>
          </div>

          <div class="mb-4">
            <div class="mb-1">
              {{ $t('frontend_custom_section_custom_css') }}<br/>
              <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
            </div>

            <text-area 
              variant="dark"  
              v-model="localStyles.advanced.fields[field.id].customStyles"
              rows="4"
            />
          </div>
        </div>
      </expansion-panel>

      <expansion-panel
        :title="`Sub Headings`"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.subheading.text"
            :fontData="fontData"
            @change="updateText('subheading', '', ...arguments)"
          ></text-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('subheading', '', ...arguments)"
            :values="spacingValues('subheading')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.subheading.customStyles"
            rows="4"
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="`Subheading: ${getTextElementTitle(field.id)}`"
        class="text-element-tab text-secondary-invert border-secondary"
        v-if="advancedMode && field.fieldtype == 'subheading'"
        v-for="(field, index) in subheadings"
        :key="field.id"
        :flat="true"
      >
        <gd-button
          class="mb-2"  
          variant="primary"
          size="auto"
          @click='activateAdvancedField(field)'
          v-if="!localStyles.advanced.fields[field.id]"

        >{{ $t('add_customization') }}</gd-button>
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='deactivateAdvancedField(field.id)'
          v-if="localStyles.advanced.fields[field.id]"
        >{{ $t('remove_customization') }}</gd-button>
        <div v-if="localStyles.advanced.fields[field.id]">
          <div class="mb-4">
            <div class="mb-1">{{ $t('Text') }}</div>
            <text-section
              :styles="localStyles.advanced.fields[field.id].text"
              :fontData="fontData"
              @change="updateText('subheading', field.id, ...arguments)"
            ></text-section>
          </div>

          <div class="mb-4">
            <div class="mb-1">
              {{ $t('frontend_custom_section_custom_css') }}<br/>
              <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
            </div>
            <text-area 
              variant="dark"  
              v-model="localStyles.advanced.fields[field.id].customStyles"
              rows="4"
            />
          </div>
        </div>
      </expansion-panel>
      <expansion-panel
        :title="$t('frontend_paragraphs')"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.paragraphs.text"
            :fontData="fontData"
            @change="updateText('paragraphs', '', ...arguments)"
          ></text-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('paragraphs', '', ...arguments)"
            :values="spacingValues('paragraphs')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.paragraphs.customStyles"
            rows="4"
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="`Paragraph: ${getTextElementTitle(field.id)}`"
        class="text-element-tab text-secondary-invert border-secondary"
        v-if="advancedMode && field.fieldtype == 'paragraph'"
        v-for="(field, index) in paragraphs"
        :key="field.id"
        :flat="true"
      >
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='activateAdvancedField(field)'
          v-if="!localStyles.advanced.fields[field.id]"
        >{{ $t('add_customization') }}</gd-button>
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='deactivateAdvancedField(field.id)'
          v-if="localStyles.advanced.fields[field.id]"
        >{{ $t('remove_customization') }}</gd-button>
        <div v-if="localStyles.advanced.fields[field.id]">
          <div class="mb-4">
            <div class="mb-1">{{ $t('Text') }}</div>
            <text-section
              :styles="localStyles.advanced.fields[field.id].text"
              :fontData="fontData"
              @change="updateText('paragraphs', field.id, ...arguments)"
            ></text-section>
          </div>

          <div class="mb-4">
            <div class="mb-1">
              {{ $t('frontend_custom_section_custom_css') }}<br/>
              <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
            </div>

            <text-area 
              variant="dark"  
              v-model="localStyles.advanced.fields[field.id].customStyles"
              rows="4"
            />
          </div>
        </div>
      </expansion-panel>
      <expansion-panel
        :title="$t('frontend_fields')"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.field.text"
            :fontData="fontData"
            @change="updateText('field', '', ...arguments)"
          ></text-section>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_label_color') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.field.labelColor"
            ></color-picker>
          </div>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_placeholder_color') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.field.placeholderColor"
            ></color-picker>
          </div>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_bg_color') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.field.backgroundColor"
            ></color-picker>
          </div>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_plugin_button_icon_color_label') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.field.iconColor"
            ></color-picker>
          </div>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('field', '', ...arguments)"
            :values="spacingValues('field')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_borders') }}</div>
          <border-section
            @change="updateBorder('field', '', ...arguments)"
            :styles="localStyles.field.border"
          ></border-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_groovemember_stylesmenu_shadowsection_shadow') }}</div>
          <shadow-section
            :styles="getShadowStyles(localStyles.field)"
            @change="updateShadow('field', '', ...arguments)"
          ></shadow-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.field.customStyles" 
            rows="4" 
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="`${$t('frontend_groovemail_campains_show_field')}: ${field.identifier}`"
        v-if="advancedMode && !['paragraph', 'heading', 'subheading'].includes(field.fieldtype)"
        v-for="field in localFields"
        :key="field.id"
        :flat="true"
      >
        <gd-button
          class="mb-2" 
          variant="primary"
          size="auto"
          @click='activateAdvancedField(field)'
          v-if="!localStyles.advanced.fields[field.id]"
        >{{ $t('add_customization') }}</gd-button>
        <gd-button
          class="mb-2"
          variant="primary"
          size="auto"
          @click='deactivateAdvancedField(field.id)'
          v-if="localStyles.advanced.fields[field.id]"
        >{{ $t('remove_customization') }}</gd-button>
        <div v-if="localStyles.advanced.fields[field.id]">
          <div class="mb-4">
            <div class="mb-1">{{ $t('Text') }}</div>
            <text-section
              :styles="localStyles.advanced.fields[field.id].text"
              :fontData="fontData"
              @change="updateText('field', field.id, ...arguments)"
            ></text-section>
          </div>
          <div class="flex flex-row pb-2">
            <span class="flex-grow">{{ $t('frontend_label_color') }}</span>
            <div class="w-20">
              <color-picker
                v-model="localStyles.advanced.fields[field.id].labelColor"
              ></color-picker>
            </div>
          </div>
          <div class="flex flex-row pb-2">
            <span class="flex-grow">{{ $t('frontend_placeholder_color') }}</span>
            <div class="w-20">
              <color-picker
                v-model="localStyles.advanced.fields[field.id].placeholderColor"
              ></color-picker>
            </div>
          </div>
          <div class="flex flex-row pb-2">
            <span class="flex-grow">{{ $t('frontend_bg_color') }}</span>
            <div class="w-20">
              <color-picker
                v-model="localStyles.advanced.fields[field.id].backgroundColor"
              ></color-picker>
            </div>
          </div>
          <div class="flex flex-row pb-2">
            <span class="flex-grow">{{ $t('frontend_plugin_button_icon_color_label') }}</span>
            <div class="w-20">
              <color-picker
                v-model="localStyles.advanced.fields[field.id].iconColor"
              ></color-picker>
            </div>
          </div>
          <div class="mb-4">
            <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
            <spacing-style
              class="text-base"
              :margin-options="marginValues"
              :padding-options="paddingValues"
              @set-style="updateSpacing('field', field.id, ...arguments)"
              :values="spacingValues('field', field.id)"
            ></spacing-style>
          </div>
          <div
            class="mb-4"
            v-if="
              field.type != 'accept' &&
              field.type != 'checkbox' &&
              field.type != 'radio'
            "
          >
            <div class="mb-1">{{ $t('frontend_style_editor_borders') }}</div>
            <border-section
              @change="updateBorder('field', field.id, ...arguments)"
              :styles="localStyles.advanced.fields[field.id].border"
            ></border-section>
          </div>
          <div class="mb-4">
            <div class="mb-1">{{ $t('frontend_groovemember_stylesmenu_shadowsection_shadow') }}</div>
            <shadow-section
              :styles="getShadowStyles(localStyles.advanced.fields[field.id])"
              @change="updateShadow('field', field.id, ...arguments)"
            ></shadow-section>
          </div>
          <div class="mb-4">
            <div class="mb-1">
              {{ $t('frontend_custom_section_custom_css') }}<br/>
              <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
            </div>
            <text-area 
              variant="dark"  
              v-model="localStyles.advanced.fields[field.id].customStyles" 
              rows="4" 
            />
          </div>
        </div>
      </expansion-panel>
      <expansion-panel
        :title="$t('frontend_groovemail_email_builder_compose_your_email_button_option')"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.button.text"
            :fontData="fontData"
            @change="updateText('button', '', ...arguments)"
          ></text-section>
        </div>
        <div class="pb-2">
          {{ $t('frontend_size') }}
          <div class="flex flex-row justify-center pt-2 pb-4">
            <gd-button
              v-for="(type, position) in sizeOptions"
              class="w-full"
              :key="type.value"
              size="sm"
              :variant="
                JSON.stringify(localStyles.button.padding) ==
                  JSON.stringify(buttonSizes[type.value].padding) &&
                JSON.stringify(localStyles.button.text.fontSize) ==
                  JSON.stringify(buttonSizes[type.value].fontSize) ? 'primary' : 'dark'
              "
              :group-position="getButtonGroupPosition(position, sizeOptions)"
              @click="buttonSize(type.value)"
            >
              {{ type.text }}
            </gd-button>
          </div>
        </div>
        <div class="pb-2">
          {{ $t('frontend_plugin_divider_align_label') }}
          <div class="flex flex-row justify-center pt-2 pb-4">
            <gd-button
              v-for="(type, position) in alignmentOptions"
              class="w-full"
              :key="type.value"
              size="sm"
              :variant="localStyles.button.alignment === type.value ? 'primary' : 'dark'"
              :group-position="getButtonGroupPosition(position, alignmentOptions)"
              @click="localStyles.button.alignment = type.value"
            >
              {{ type.text }}
            </gd-button>
          </div>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_bg_color') }}</span>
          <div class="w-20">
            <color-picker
              v-model="localStyles.button.backgroundColor"
            ></color-picker>
          </div>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('button', '', ...arguments)"
            :values="spacingValues('button')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_borders') }}</div>
          <border-section
            @change="updateBorder('button', '', ...arguments)"
            :styles="localStyles.button.border"
          ></border-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_groovemember_stylesmenu_shadowsection_shadow') }}</div>
          <shadow-section
            :styles="getShadowStyles(localStyles.button)"
            @change="updateShadow('button', '', ...arguments)"
          ></shadow-section>
        </div>
        <div class="flex flex-row pb-2">
          <span class="flex-grow">{{ $t('frontend_builder_text_section_animation') }}</span>
          <div class="w-32">
            <dropdown-with-scroll
              v-model="localStyles.button.animation"
              :options="buttonAnimationaOptions"
              class="w-full text-left text-secondary-invert text-sm"
              dark
              maxHeight="150px"
            ></dropdown-with-scroll>
          </div>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.button.customStyles"
            rows="4"
          />
        </div>
      </expansion-panel>
      <expansion-panel
        :title="$t('frontend_button_sublabel')"
        v-if="buttonSublabel"
        :flat="true"
      >
        <div class="mb-4">
          <div class="mb-1">{{ $t('Text') }}</div>
          <text-section
            :styles="localStyles.button_sublabel.text"
            :fontData="fontData"
            @change="updateText('button_sublabel', '', ...arguments)"
          ></text-section>
        </div>
        <div class="mb-4">
          <div class="mb-1">{{ $t('frontend_style_editor_spacing') }}</div>
          <spacing-style
            class="text-base"
            :margin-options="marginValues"
            :padding-options="paddingValues"
            @set-style="updateSpacing('button_sublabel', '', ...arguments)"
            :values="spacingValues('button_sublabel')"
          ></spacing-style>
        </div>
        <div class="mb-4">
          <div class="mb-1">
            {{ $t('frontend_custom_section_custom_css') }}<br/>
            <span class="text-xs">{{ $t('frontend_custom_css_message') }}</span>
          </div>
          <text-area 
            variant="dark"  
            v-model="localStyles.button_sublabel.customStyles" 
            rows="4" 
          />
        </div>
     </expansion-panel>
   </expansion-group>
    </div>
  </div>
</template>
<script>
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker';
import ExpansionPanel from '@groovepages/gd-ui-kit/components/ExpansionPanel.vue';
import ExpansionGroup from '@groovepages/gd-ui-kit/components/ExpansionGroup.vue';
import SpacingStyle from './form-style-elements/SpacingSection';
import TextSection from './form-style-elements/TextSection';
import BorderSection from './form-style-elements/BorderSection';
import ShadowSection from './form-style-elements/ShadowSection';
import BackgroundSection from './form-style-elements/BackgroundSection';
import 'animate.css';

import DropdownWithScroll from './DropdownWithScroll';

// import icons
import CloseIcon from '@/assets/images/icons/new/close_small_new.svg';

export default {
  name: 'GroovemailFormStyle',
  components: {
    BackgroundSection,
    BorderSection,
    TextSection,
    ShadowSection,
    DropdownWithScroll,
    SpacingStyle,
    CloseIcon,
    ColorPicker,
    ExpansionPanel,
    ExpansionGroup,
  },
  props: {
    fields: {
      required: true,
    },
    buttonSublabel: {
      required: false,
    },
    styles: {
      required: true,
    },
    fontData: {
      required: true,
    }
  },
  data(){
    return {
      advancedMode: false,
      buttonSizes: {
        'small': {
          padding: [4,4,4,4],
          fontSize: 13,
          sublabelFontSize: 10,
        },
        'medium': {
          padding: [8,8,8,8],
          fontSize: 16,
          sublabelFontSize: 13,
        },
        'large': {
          padding: [18,18,18,18],
          fontSize: 21,
          sublabelFontSize: 18,
        },
      },
      marginValues: [...Array(100).keys()].map(i => String(i)),
      paddingValues: [...Array(100).keys()].map(i => String(i)),
      localStyles: {},
      initialStyles: {},
      localFields: this.fields.filter(f => f.id !== 'submit'),
      sizeOptions: [
        { text: 'Small', value: 'small' },
        { text: 'Medium', value: 'medium' },
        { text: 'Large', value: 'large' },
      ],
      textAlignmentOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Center', value: 'center' },
        { text: 'Right', value: 'right' },
      ],
      alignmentOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Center', value: 'center' },
        { text: 'Right', value: 'right' },
        { text: 'Full', value: 'full' },
      ],
    }
  },
  watch: {
    fields: {
      deep: true,
      handler(){
        this.localFields = this.fields;
      }
    },
    styles: {
      immediate: true,
      deep: true,
      handler(){
        this.localStyles = JSON.parse(JSON.stringify(this.styles));
        if (!this.localStyles.paragraphs.padding){
          this.$set(this.localStyles.paragraphs, 'padding', [0,0,0,0]);
        }
        if (!this.localStyles.paragraphs.margin){
          this.$set(this.localStyles.paragraphs, 'margin', [0,0,0,0]);
        }
        if (!this.localStyles.button.text.fontFamily){
          this.$set(this.localStyles.button.text, 'fontFamily', 'default');
        }
        if (!this.localStyles.button_sublabel.text.fontFamily){
          this.$set(this.localStyles.button_sublabel.text, 'fontFamily', 'default');
        }
        this.initialStyles = JSON.parse(JSON.stringify(this.styles));
      }
    },
    localStyles: {
      deep: true,
      handler(){
        let styles = JSON.parse(JSON.stringify(this.localStyles));
        //  styles.advanced.fields = styles.advanced.fields.filter(s => s !== {});o
        if(JSON.stringify(styles) != JSON.stringify(this.initialStyles)){
          this.$emit('changed', styles);
        }
      }
    }
  },
  mounted: function() {
    //
  },
  computed: {
    headings() {
      return this.localFields.filter((e) => e.fieldtype == 'heading');
    },
    subheadings() {
      return this.localFields.filter((e) => e.fieldtype == 'subheading');
    },
    paragraphs() {
      return this.localFields.filter((e) => e.fieldtype == 'paragraph');
    },
    buttonAnimationaOptions() {
      return [
        { text: this.$t('frontend_groovevideo_animation_none'), value: 'none' },
        { text: this.$t('frontend_groovevideo_animation_tada'), value: 'tada' },
        { text: this.$t('frontend_groovevideo_animation_pulse'), value: 'pulse' },
        { text: this.$t('frontend_groovevideo_animation_zoomIn'), value: 'zoomIn' },
        { text: this.$t('frontend_groovevideo_animation_jello'), value: 'jello' },
        { text: this.$t('frontend_groovevideo_animation_wobble'), value: 'wobble' },
        { text: this.$t('frontend_groovevideo_animation_swing'), value: 'swing' },
        { text: this.$t('frontend_groovevideo_animation_bounceIn'), value: 'bounceIn' },
        { text: this.$t('frontend_groovevideo_animation_bounce'), value: 'bounce' },
        { text: this.$t('frontend_groovevideo_animation_flash'), value: 'flash' },
        { text: this.$t('frontend_groovevideo_animation_headshake'), value: 'headShake' },
        { text: this.$t('frontend_groovevideo_animation_heartbeat'), value: 'heartBeat' },
        { text: this.$t('frontend_groovevideo_animation_shakeX'), value: 'shakeX' },
        { text: this.$t('frontend_groovevideo_animation_shakeY'), value: 'shakeY' },
      ];
    },
  },
  methods: {
    buttonSize(size) {
      this.localStyles.button.padding = this.buttonSizes[size].padding;
      this.localStyles.button.text.fontSize = this.buttonSizes[size].fontSize;
      this.localStyles.button_sublabel.text.fontSize = this.buttonSizes[size].sublabelFontSize;
    },
    getButtonGroupPosition(position, variable) {
      if (position === 0) return 'left';
      if (position === variable.length - 1) return 'right';
      return 'middle';
    },
    activateAdvancedField(field){
      if(JSON.stringify(this.localStyles.advanced.fields) == '[]'){
        this.localStyles.advanced.fields = {};
      }
      switch (field.type) {
        case 'paragraph':
          this.$set(
            this.localStyles.advanced.fields,
            field.id,
            JSON.parse(JSON.stringify(this.localStyles.paragraphs))
          );
          break;
        case 'heading':
          this.$set(
            this.localStyles.advanced.fields,
            field.id,
            JSON.parse(JSON.stringify(this.localStyles.heading))
          );
          break;
          case 'subheading':
          this.$set(
            this.localStyles.advanced.fields,
            field.id,
            JSON.parse(JSON.stringify(this.localStyles.subheading))
          );
          break;
        default:
          this.$set(
            this.localStyles.advanced.fields,
            field.id,
            JSON.parse(JSON.stringify(this.localStyles.field))
          );
      }
    },
    deactivateAdvancedField(field){

      this.localStyles.advanced.fields[field] = null;
    },
    formBackgroundValues() {
      return {
        backgroundColor: this.localStyles.form.backgroundColor,
        backgroundPattern: this.localStyles.form.backgroundPattern ?? 'null',
        backgroundPatternFrontColor: this.localStyles.form.backgroundPatternFrontColor ?? 'rgba(0, 0, 0, 1)',
        backgroundPatternBackColor: this.localStyles.form.backgroundPatternBackColor ?? 'rgba(255, 255, 255, 1)',
        backgroundPatternOpacity: this.localStyles.form.backgroundPatternOpacity ?? 20,
        backgroundPatternSpacing: this.localStyles.form.backgroundPatternSpacing ?? 50,
        backgroundBlur: this.localStyles.form.backgroundBlur ?? 0,
        backgroundRepeat: this.localStyles.form.backgroundRepeat ?? 'repeat',
        backgroundSize: this.localStyles.form.backgroundSize ?? 'cover',
        backgroundPosition: this.localStyles.form.backgroundPosition ?? '50%',
        backgroundImage: this.localStyles.form.backgroundImage ?? null,
      }
    },
    updateFormBackgroundValues(values) {
      this.$set(this.localStyles.form, 'backgroundColor', values.backgroundColor);
      this.$set(this.localStyles.form, 'backgroundPattern', values.backgroundPattern);
      this.$set(this.localStyles.form, 'backgroundPatternFrontColor', values.backgroundPatternFrontColor);
      this.$set(this.localStyles.form, 'backgroundPatternBackColor', values.backgroundPatternBackColor);
      this.$set(this.localStyles.form, 'backgroundPatternOpacity', values.backgroundPatternOpacity);
      this.$set(this.localStyles.form, 'backgroundPatternSpacing', values.backgroundPatternSpacing);
      this.$set(this.localStyles.form, 'backgroundBlur', values.backgroundBlur);
      this.$set(this.localStyles.form, 'backgroundRepeat', values.backgroundRepeat);
      this.$set(this.localStyles.form, 'backgroundSize', values.backgroundSize);
      this.$set(this.localStyles.form, 'backgroundPosition', values.backgroundPosition);
      this.$set(this.localStyles.form, 'backgroundImage', values.backgroundImage);
    },
    spacingValues(element, field_id = null) {
      let ret = {};
      if(!field_id){
        if(!this.localStyles[element].margin || !this.localStyles[element].padding){
          return false;
        }else{
          if(!Array.isArray(this.localStyles[element].margin)){
            this.localStyles[element].margin = [0,0,0,0];
          }
          if(!Array.isArray(this.localStyles[element].padding)){
            this.localStyles[element].padding = [0,0,0,0];
          }
          ret = {
            margin: this.localStyles[element].margin,
            padding: this.localStyles[element].padding
          };
        }
      } else {
        ret = {
          margin: this.localStyles.advanced.fields[field_id].margin,
          padding: this.localStyles.advanced.fields[field_id].padding
        }
      }
      return {
        paddingTop: String(ret.padding[0]),
        paddingRight: String(ret.padding[1]),
        paddingBottom: String(ret.padding[2]),
        paddingLeft: String(ret.padding[3]),
        marginTop: String(ret.margin[0]),
        marginRight: String(ret.margin[1]),
        marginBottom: String(ret.margin[2]),
        marginLeft: String(ret.margin[3]),
      };
    },
    updateText(element, advancedId = '', value){
      if(!advancedId){
        this.$set(this.localStyles[element], 'text', value);
        return;
      }
      this.$set(this.localStyles.advanced.fields[advancedId], 'text', value);
    },
    getShadowStyles(element, advancedId = '', value){
      return element.boxShadow ?? 'none';
      if (!advancedId) {
        return this.localStyles[element]?.boxShadow ?? 'none';
      }
      return this.localStyles.advanced.fields[advancedId]?.boxShadow ?? 'none';
    },
    updateShadow(element, advancedId = '', value){
      if(!advancedId){
        this.$set(this.localStyles[element], 'boxShadow', value);
        return;
      }
      this.$set(this.localStyles.advanced.fields[advancedId], 'boxShadow', value);
    },
    updateBorder(element, advancedId = '', value){
      if(!advancedId){
        this.$set(this.localStyles[element], 'border', value);
        return;
      }
      this.$set(this.localStyles.advanced.fields[advancedId], 'border', value);
    },
    updateSpacing(element, advancedId = '', value){
      let positions = ['Top', 'Right', 'Bottom', 'Left'];
      let property = value.property.includes('margin') ? 'margin' : 'padding';
      let position = positions.indexOf(value.property.replace(property, ''));

      if(!advancedId){
        this.localStyles[element][property] = [
          position === 0 ? Number(value.value) : this.localStyles[element][property][0],
          position === 1 ? Number(value.value) : this.localStyles[element][property][1],
          position === 2 ? Number(value.value) : this.localStyles[element][property][2],
          position === 3 ? Number(value.value) : this.localStyles[element][property][3]
        ];
        return;
      }
      this.localStyles.advanced.fields[advancedId][property] = [
        position === 0 ? Number(value.value) : this.localStyles.advanced.fields[advancedId][property][0],
        position === 1 ? Number(value.value) : this.localStyles.advanced.fields[advancedId][property][1],
        position === 2 ? Number(value.value) : this.localStyles.advanced.fields[advancedId][property][2],
        position === 3 ? Number(value.value) : this.localStyles.advanced.fields[advancedId][property][3]
      ];
    },
    getTextElementTitle(id){
      return document.getElementById(id).innerText;
    },
    close(){
      this.$emit('close');
    }
  }
};
</script>
<style>
.groovemail-modal{
  width:300px;
  position: fixed;
  top: 64px;
  right: 0px;
  z-index: 60;
}

.text-element-tab .heading-md>div {
  width: 100%;
}
.text-element-tab .heading-md>div>span {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
</style>
